// styles.js
// Shared styles for EditListing components

import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

export const styles = {
  // Page Container
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: LAYOUT.background,
    padding: '40px 20px',
  },

  // Main Container
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },

  // Header Section
  headerSection: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, #a00e1f 100%)`,
    padding: '48px 40px',
    textAlign: 'center',
    color: '#ffffff',
  },

  iconWrapper: {
    width: '64px',
    height: '64px',
    margin: '0 auto 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },

  heading: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 12px 0',
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },

  subheading: {
    fontSize: '16px',
    fontWeight: '400',
    margin: 0,
    color: 'rgba(255, 255, 255, 0.9)',
    letterSpacing: '0.2px',
  },

  // Form
  form: {
    padding: '40px',
  },

  formCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    padding: '32px',
    marginBottom: '24px',
  },

  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 24px 0',
    paddingBottom: '12px',
    borderBottom: `2px solid ${COLORS.primary}`,
  },

  // Input Groups
  inputGroup: {
    marginBottom: '24px',
  },

  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.secondary,
    marginBottom: '8px',
    letterSpacing: '0.3px',
  },

  required: {
    color: COLORS.primary,
    marginLeft: '4px',
  },

  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: TYPOGRAPHY.fontFamily,
    boxSizing: 'border-box',
    ':focus': {
      borderColor: COLORS.primary,
      backgroundColor: '#ffffff',
    },
  },

  textarea: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: TYPOGRAPHY.fontFamily,
    resize: 'vertical',
    minHeight: '120px',
    lineHeight: '1.6',
    boxSizing: 'border-box',
  },

  select: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: TYPOGRAPHY.fontFamily,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },

  // Two Column Grid
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },

  // Price Input
  priceInputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  dollarSign: {
    position: 'absolute',
    left: '16px',
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.secondary,
    pointerEvents: 'none',
    zIndex: 1,
  },

  priceInput: {
    width: '100%',
    padding: '14px 16px 14px 32px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: TYPOGRAPHY.fontFamily,
    boxSizing: 'border-box',
  },

  // Image Manager
  imageManagerContainer: {
    marginTop: '16px',
  },

  uploadArea: {
    border: `2px dashed ${COLORS.primary}`,
    borderRadius: '12px',
    padding: '48px 24px',
    textAlign: 'center',
    backgroundColor: '#fef5f6',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  uploadAreaDragging: {
    backgroundColor: '#fce4e7',
    borderColor: '#a00e1f',
    transform: 'scale(1.02)',
  },

  uploadIcon: {
    color: COLORS.primary,
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center',
  },

  uploadText: {
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 8px 0',
  },

  uploadHint: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },

  hiddenInput: {
    display: 'none',
  },

  // Preview Section
  previewSection: {
    marginTop: '32px',
  },

  previewLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.secondary,
    marginBottom: '16px',
  },

  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '16px',
  },

  imagePreviewItem: {
    position: 'relative',
    paddingTop: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '2px solid #e0e0e0',
    transition: 'all 0.2s ease',
    backgroundColor: '#f5f5f5',
  },

  imagePreview: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  primaryBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: COLORS.primary,
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '6px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    zIndex: 2,
  },

  removeImageButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    ':hover': {
      backgroundColor: COLORS.primary,
    },
  },

  // Submit Button
  submitButton: {
    width: '100%',
    padding: '18px 32px',
    fontSize: '16px',
    fontWeight: '700',
    color: '#ffffff',
    backgroundColor: COLORS.primary,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '0.5px',
    marginTop: '16px',
    boxShadow: '0 4px 12px rgba(206, 17, 38, 0.3)',
    ':hover': {
      backgroundColor: '#a00e1f',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(206, 17, 38, 0.4)',
    },
  },

  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
    ':hover': {
      backgroundColor: COLORS.primary,
      transform: 'none',
    },
  },

  // Loading State
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: LAYOUT.background,
  },

  loadingContent: {
    textAlign: 'center',
    padding: '48px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },

  spinnerAnimation: {
    color: COLORS.primary,
    animation: 'spin 1.5s linear infinite',
    marginBottom: '20px',
  },

  loadingText: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 8px 0',
  },

  loadingSubtext: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },

  // Keyframes (needs to be added to global CSS)
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};